import { supabase } from './supabaseClient'
import {
  ENROLLMENT_ACCESSIBLE_STATUSES,
  ENROLLMENT_VISIBLE_STATUSES,
  normalizeEnrollmentStatus,
} from './enrollmentStatus'

const PERSONAL_SCOPED_ROLES = new Set(['Ponto Focal'])
export const COURSE_VISIBLE_ENROLLMENT_STATUSES = ENROLLMENT_VISIBLE_STATUSES
export const COURSE_ACCESSIBLE_ENROLLMENT_STATUSES = ENROLLMENT_ACCESSIBLE_STATUSES

function normalizeEmail(value) {
  return String(value || '').trim().toLowerCase()
}

function isPersonalScopedRole(roleName) {
  return PERSONAL_SCOPED_ROLES.has(String(roleName || '').trim())
}

function matchesAllowedStatuses(status, allowedStatuses) {
  if (!Array.isArray(allowedStatuses) || allowedStatuses.length === 0) return true
  return allowedStatuses.includes(String(status || ''))
}

function mapDirectEnrollmentRow(row) {
  return {
    id: row?.id || null,
    status: normalizeEnrollmentStatus(row?.status, row?.expires_at),
    created_at: row?.created_at || null,
    expires_at: row?.expires_at || null,
    offering_id: row?.offering_id || null,
    student_id: row?.student_id || null,
    progress_percent: Number(row?.progress_percent) || 0,
    student: {
      full_name: row?.student?.full_name || '',
      email: row?.student?.email || '',
    },
    offering: {
      id: row?.offering?.id || null,
      course_id: row?.offering?.course_id || null,
      course: {
        name: row?.offering?.course?.name || '',
      },
    },
    classId: null,
    className: '-',
  }
}

function mapScopedEnrollmentRow(row) {
  return {
    id: row?.id || null,
    status: normalizeEnrollmentStatus(row?.status, row?.expires_at),
    created_at: row?.created_at || null,
    expires_at: row?.expires_at || null,
    offering_id: row?.offering_id || null,
    student_id: row?.student_id || null,
    progress_percent: 0,
    student: {
      full_name: row?.student_full_name || '',
      email: row?.student_email || '',
    },
    offering: {
      id: row?.offering_id || null,
      course_id: row?.course_id || null,
      course: {
        name: row?.course_name || '',
      },
    },
    classId: row?.class_id || null,
    className: row?.class_name || '-',
  }
}

function belongsToCurrentLearner(row, auth) {
  const currentUserId = String(auth?.session?.user?.id || '')
  const currentEmail = normalizeEmail(auth?.session?.user?.email || auth?.profile?.email)
  const rowUserId = String(row?.student_id || '')
  const rowEmail = normalizeEmail(row?.student_email || row?.student?.email)

  if (currentUserId && rowUserId && currentUserId === rowUserId) return true
  if (currentEmail && rowEmail && currentEmail === rowEmail) return true
  return false
}

async function attachClassMetadata(enrollments) {
  return Promise.all((enrollments || []).map(async (enrollment) => {
    if (enrollment?.classId || !enrollment?.student_id || !enrollment?.offering?.course_id) {
      return enrollment
    }

    const { data: classData } = await supabase
      .from('classes')
      .select('id, name, class_students!inner(student_id), class_courses!inner(course_id)')
      .eq('class_students.student_id', enrollment.student_id)
      .eq('class_courses.course_id', enrollment.offering.course_id)
      .limit(1)
      .maybeSingle()

    return {
      ...enrollment,
      className: classData?.name || enrollment?.className || '-',
      classId: classData?.id || enrollment?.classId || null,
    }
  }))
}

async function listDirectOwnEnrollments(auth, { confirmedOnly = false, allowedStatuses = null } = {}) {
  const userId = auth?.session?.user?.id || null
  if (!userId) return []

  let query = supabase
    .from('enrollments')
    .select(`
      id,
      status,
      created_at,
      expires_at,
      offering_id,
      student_id,
      progress_percent,
      student:student_id (full_name, email),
      offering:offering_id (
        id,
        course_id,
        course:course_id (name)
      )
    `)
    .eq('student_id', userId)
    .order('created_at', { ascending: false })

  const { data, error } = await query
  if (error) throw error

  const normalizedRows = (data || []).map(mapDirectEnrollmentRow)
  return (Array.isArray(allowedStatuses) && allowedStatuses.length > 0)
    ? normalizedRows.filter((row) => matchesAllowedStatuses(row?.status, allowedStatuses))
    : confirmedOnly
      ? normalizedRows.filter((row) => String(row?.status || '') === 'Ativa')
      : normalizedRows
}

async function listScopedOwnEnrollments(auth, { confirmedOnly = false, allowedStatuses = null } = {}) {
  const roleName = auth?.profileRole || auth?.profile?.role || ''
  if (!isPersonalScopedRole(roleName)) return []

  const companyId = auth?.profile?.companyId || null
  if (!companyId) return []

  const { data, error } = await supabase.rpc('admin_list_enrollments_by_scope', {
    p_company_id: companyId,
    p_partner_id: null,
  })
  if (error) throw error

  const scopedRows = (data || [])
    .filter((row) => belongsToCurrentLearner(row, auth))
    .map(mapScopedEnrollmentRow)
  const normalizedRows = (Array.isArray(allowedStatuses) && allowedStatuses.length > 0)
    ? scopedRows.filter((row) => matchesAllowedStatuses(row?.status, allowedStatuses))
    : confirmedOnly
      ? scopedRows.filter((row) => String(row?.status || '') === 'Ativa')
      : scopedRows

  return normalizedRows
}

export async function listMyVisibleEnrollments(auth, options = {}) {
  const [directResult, scopedResult] = await Promise.allSettled([
    listDirectOwnEnrollments(auth, options),
    listScopedOwnEnrollments(auth, options),
  ])

  if (directResult.status === 'rejected' && scopedResult.status === 'rejected') {
    throw (scopedResult.reason || directResult.reason || new Error('Falha ao carregar matrículas visíveis'))
  }

  const directRows = directResult.status === 'fulfilled' ? directResult.value : []
  const scopedRows = scopedResult.status === 'fulfilled' ? scopedResult.value : []

  const merged = new Map()
  for (const row of [...scopedRows, ...directRows]) {
    if (!row?.id) continue
    merged.set(String(row.id), row)
  }

  const enrichedRows = await attachClassMetadata(Array.from(merged.values()))
  return enrichedRows.sort((a, b) => {
    const left = new Date(a?.created_at || 0).getTime()
    const right = new Date(b?.created_at || 0).getTime()
    return right - left
  })
}
